/* eslint-disable camelcase */
import { createAction } from '@reduxjs/toolkit';
import { AppDispatch, AppState } from 'app/store';
import { API_ENDPOINTS, STORAGE_KEYS } from 'utils/constants';
import { setInStorage, logErrors } from 'utils';
import { LooseObjectType } from 'utils/globalTypes';
import { InitialValues } from 'pages/signin';
import { deleteAPI, getAPI, postAPI } from 'services/api/apiAsync';
import formatEntitlements, {
  formatUserFeatures,
  getAgentIdFromAccessToken,
  getXsrfTokenFromJwt,
  getSessionIdFromAccessToken,
} from 'services/session/utils';
import { doLockVault } from 'services/vault/vaultAsync';
import { setBrand } from 'services/brand/brandSlice';
import type {
  SessionSliceType,
  TourAttributeUpdate,
  TourUserAttributes,
} from './types';
import {
  setSessionSlice,
  refreshTokenUpdate,
  setTourAttributes,
} from './sessionSlice';
import { selectRefreshToken, selectToken } from './sessionSelectors';

const signOutRequest = createAction('SIGNOUT_REQUEST');

export function storeUserSessionDetails(response: LooseObjectType) {
  return async (dispatch: AppDispatch) => {
    try {
      const { token, accessToken, refreshToken, profile } = response;
      const { missingPii } = profile;
      /* Captured from the sign-in `profile`, which reports the state BEFORE the
         backend stamps this login — so `lastVisitedAt` is null on a genuine
         first visit. This is the dashboard tour's first-visit signal; see the
         field docs on `SessionSliceType`. Read defensively because the
         enrollment / reset-password callers may not carry these fields. */
      const lastVisitedAt = profile?.lastVisitedAt ?? null;
      /* Durable tour flags. Confirmed top-level on the wire (not under
         `profile`, unlike `lastVisitedAt`), and sent as a bare `{}` until the
         user has skipped or opted out at least once.
         The defaults therefore matter: an absent or empty object must read as
         "never skipped, never opted out" so the tour still shows, rather than
         yielding `undefined` and suppressing it. The SSO / masquerade path
         forwards whatever `api/client/sessions/redeem` returned, so it lands
         here on the same terms as password sign-in. */
      const userAttributes: TourUserAttributes = {
        skipTourCount: 0,
        dontShowTour: false,
        ...(response.userAttributes ?? {}),
      };
      const xsrfToken = getXsrfTokenFromJwt(token);
      const entitlements = formatEntitlements(
        accessToken,
      ) as SessionSliceType['entitlements'];
      const features = formatUserFeatures(entitlements);
      const agentId = getAgentIdFromAccessToken(accessToken);
      const sessionId = getSessionIdFromAccessToken(accessToken);

      // update session and entitlements
      dispatch(
        setSessionSlice({
          token: xsrfToken,
          userLoggedIn: true,
          agentId,
          entitlements,
          accessToken,
          refreshToken,
          features,
          missingPii,
          lastVisitedAt,
          userAttributes,
          sessionId,
        }),
      );
      return undefined;
    } catch (error) {
      return Promise.reject(error);
    }
  };
}

/**
 * Persists ONE of the tour's flags.
 *
 * Deliberately partial: the caller sends only the attribute the user actually
 * changed — `{ skipTourCount }` for Skip, `{ dontShowTour: true }` for
 * "Don't show again" and Finish. Sending both would let a Skip overwrite
 * `dontShowTour` with whatever value this client happened to be holding, which
 * could be stale if it changed elsewhere.
 *
 *
 * `POST api/client/users/attribute` answers **204 No Content**, so
 * `makeAPIRequest` resolves to a falsy `response.data`. The value is therefore
 * ignored entirely — never test it for truthiness, or a successful save reads as
 * a failure.
 *
 * The store is updated only after the request resolves (CLAUDE.md rule 6), so a
 * failed save leaves the count as the server still has it. Rejection is
 * re-thrown for the caller to surface: the tour needs to know, because it shows
 * a banner and skips its exit reload on failure.
 */
export function saveTourAttributes(update: TourAttributeUpdate) {
  return async (dispatch: AppDispatch) => {
    try {
      await dispatch(
        postAPI({ url: API_ENDPOINTS.userAttribute, data: update }),
      ).unwrap();
      /* The reducer merges, so the untouched flag keeps its current value. */
      dispatch(setTourAttributes(update));
      return undefined;
    } catch (error) {
      logErrors(error);
      return Promise.reject(error);
    }
  };
}

// user signin
export function createUserSession(values: InitialValues) {
  return async (dispatch: AppDispatch) => {
    try {
      const config = {
        url: API_ENDPOINTS.userSession,
        data: {
          username: values.username,
          password: values.password,
        },
      };
      const response = await dispatch(postAPI(config)).unwrap();

      return response;
    } catch (error) {
      return Promise.reject(error);
    }
  };
}

// clear slice data on sign out
export function clearStore() {
  return async (dispatch: AppDispatch, getState: AppState) => {
    const { brand } = getState();
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { companyDetails, ...brandDetails } = brand;
    dispatch(signOutRequest()); // To reset the all the slice state to initial values
    dispatch(setBrand(brandDetails)); // To set the brand details
  };
}

// user sign out
export function clearSession() {
  return async (dispatch: AppDispatch, getState: AppState) => {
    try {
      const store = getState();
      const refreshToken = selectRefreshToken(store);
      const token = selectToken(store);
      const config = {
        url: API_ENDPOINTS.userSession,
        headers: {
          'X-XSRF-TOKEN': token,
          REFRESH_TOKEN: refreshToken,
        },
      };
      try {
        dispatch(doLockVault());
      } catch (error) {
        logErrors(error);
      }
      try {
        await dispatch(deleteAPI(config)).unwrap();
        dispatch(clearStore());
      } catch (error) {
        dispatch(clearStore());
      }
      return null;
    } catch (error) {
      return Promise.reject(error);
    }
  };
}

/**
 * Makes POST request to /client/sessions/refresh to refresh the access token
 * Dispatches action REFRESH_TOKEN on success
 */
export function refreshApToken() {
  return async (dispatch: AppDispatch, getState: AppState) => {
    try {
      const store = getState();
      const refreshToken = selectRefreshToken(store);
      const token = selectToken(store);
      const config = {
        url: API_ENDPOINTS.refreshToken,
        data: {
          refreshToken,
        },
        headers: {
          'X-XSRF-TOKEN': token,
        },
      };
      const response = await dispatch(postAPI(config)).unwrap();
      dispatch(refreshTokenUpdate(response));

      return response;
    } catch (error) {
      if (error === 401) {
        setInStorage(STORAGE_KEYS.sessionExpired, true);
        dispatch(clearStore());
      }
      return Promise.reject(error);
    }
  };
}

export function verifySignInTokenAndGetSessionTokens(token: string) {
  return async (dispatch: AppDispatch) => {
    try {
      const config = {
        url: API_ENDPOINTS.redeemSession,
        method: 'post',
        data: {
          signinToken: token,
        },
      };
      const response = await dispatch(postAPI(config)).unwrap();
      return response?.data ?? response;
    } catch (error) {
      return Promise.reject(error);
    }
  };
}

/**
 * Reads the tour flags off a redeem response in whichever casing it used, and
 * returns `undefined` when it carried neither — `storeUserSessionDetails` then
 * applies its own defaults rather than storing a half-built object.
 */
function normaliseTourAttributes(
  raw: LooseObjectType | undefined,
): TourUserAttributes | undefined {
  if (!raw) return undefined;
  return {
    skipTourCount: raw.skipTourCount ?? raw.skip_tour_count ?? 0,
    dontShowTour: raw.dontShowTour ?? raw.dont_show_tour ?? false,
  };
}

/**
 * Reads `missingPii` from `api/client/sessions/profile` for a freshly redeemed
 * SSO / masquerade session.
 *
 * The redeem response does not report it, so without this every SSO user
 * reached the session slice with `missingPii: []` and the data-collection
 * (missing SSN / DOB / phone) modal and activation cards never appeared.
 * Angular does the same: after `createApSession` it calls
 * `getSessionProfile()` and dispatches `UPDATE_MISSING_PII`
 * (`sign-in.component.ts:171-175`).
 *
 * The access token is passed explicitly because the session is not in the
 * store yet. Returns `undefined` on failure so sign-in is never blocked by it;
 * the caller then keeps whatever the redeem response said.
 */
function fetchSessionMissingPii(accessToken: string | undefined) {
  return async (dispatch: AppDispatch): Promise<string[] | undefined> => {
    if (!accessToken) return undefined;
    try {
      const response = await dispatch(
        getAPI({
          url: API_ENDPOINTS.sessionProfile,
          headers: { Authorization: accessToken },
        }),
      ).unwrap();
      const missingPii = response?.missingPii ?? response?.missing_pii;
      return Array.isArray(missingPii) ? missingPii : undefined;
    } catch (error) {
      logErrors(error);
      return undefined;
    }
  };
}

/**
 * Exchanges the `signin_token` in the URL for a real session. Both the partner
 * SSO hand-off and the CSR masquerade link land here from
 * `/sign-in?signin_token=`.
 *
 * The response is forwarded WHOLE to `storeUserSessionDetails` — same as
 * `pages/signin` does with its `POST api/client/sessions` response — rather
 * than being rebuilt field by field. The hand-built object this replaces
 * listed only the tokens and `profile.missingPii`, so it silently dropped
 * `profile.lastVisitedAt` and the durable `userAttributes`, and every SSO
 * sign-in reached the session slice as `{ skipTourCount: 0, dontShowTour:
 * false }`. That made the dashboard tour's server-side suppression inert for
 * SSO users: `saveTourAttributes` wrote Skip and "Don't show again" to
 * `api/client/users/attribute` correctly, but nothing ever read them back, so
 * the tour returned on the next sign-in however many times it had been
 * dismissed — and always with the first-visit ("built") copy, since
 * `lastVisitedAt` was missing too. Password sign-in was never affected.
 *
 * Only the fields whose casing differs between the two redeem shapes are
 * normalised below; everything else rides on the spread. Defaults remain where
 * the response may legitimately say nothing: `profile.missingPii`, which the
 * caller destructures, and `token`, whose fallback is the redeem token from
 * the URL.
 */
export function masqueradeUserSession(token: string) {
  return async (dispatch: AppDispatch): Promise<LooseObjectType> => {
    try {
      const response = await dispatch(
        verifySignInTokenAndGetSessionTokens(token),
      );

      const profile = response.profile ?? {};
      /* Read BOTH casings for every field the session slice needs. The two
         shapes this endpoint has answered with differ only in casing, and
         which one an environment serves is not knowable from here:
         `api/client/sessions/redeem` mirrors `POST api/client/sessions`
         (camelCase), while the `auth/session/redeem` it replaced used
         snake_case. A one-sided read is what left SSO users without their tour
         flags — the slice fell back to `{ skipTourCount: 0, dontShowTour:
         false }`, so the tour returned on every SSO sign-in however many times
         it had been skipped, while password sign-in was unaffected. */
      const userAttributes = normaliseTourAttributes(
        response.userAttributes ?? response.user_attributes,
      );
      const accessToken = response.accessToken ?? response.access_token;
      const sessionMissingPii = await dispatch(
        fetchSessionMissingPii(accessToken),
      );

      return {
        ...response,
        token: response.token ?? token,
        accessToken,
        refreshToken: response.refreshToken ?? response.refresh_token,
        userAttributes,
        profile: {
          ...profile,
          missingPii:
            sessionMissingPii ??
            profile.missingPii ??
            profile.missing_pii ??
            [],
          lastVisitedAt:
            profile.lastVisitedAt ?? profile.last_visited_at ?? null,
        },
      };
    } catch (error) {
      return Promise.reject(error);
    }
  };
}

/* Function to fetch ChatAgent scoped JWT token */
export function fetchThinSessionDetails() {
  return async (dispatch: AppDispatch) => {
    try {
      const config = {
        url: API_ENDPOINTS.thinSession,
      };

      const response = await dispatch(postAPI(config)).unwrap();
      return response;
    } catch (error) {
      return Promise.reject(error);
    }
  };
}
